import AppCard from '@nxsys/components/AppCard';
import AppLoader from '@nxsys/components/AppLoader';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  PROSPECT_DEFAULT_PAGE_SIZE,
  PROSPECT_TYPE,
} from '@nxsys/constants/prospect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import {
  AgencyProspect,
  GetAgencyProspectListParams,
} from '@nxsys/store/slices/prospect/types';
import { showToast } from '@nxsys/utils/toast';
import { Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionsHeader } from '../ActionsHeader';
import { getColumns } from '../columns';
import ViewAgencyModal from './ViewAgencyModal';

const AgencyTab = ({
  renderAddNewButton,
}: {
  renderAddNewButton: (variant: string) => React.ReactNode;
}) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const { agencyList } = useSelector((state: AppState) => state.prospectStore);
  const { loading, list, pagination, queryParams } = agencyList;

  const [selectedRows, setSelectedRows] = useState<AgencyProspect[]>([]);

  const [viewAgencyModal, setViewAgencyModal] = useState<{
    open: boolean;
    data: AgencyProspect | null;
  }>({
    open: false,
    data: null,
  });
  const toggleViewAgencyModal = () =>
    setViewAgencyModal({ ...viewAgencyModal, open: !viewAgencyModal.open });

  const [isProgressRegistration, setIsProgressRegistration] = useState(false);
  // FE-BUG-005/015: Add missing loading state for delete
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: PROSPECT_DEFAULT_PAGE_SIZE,
    });
  }, []);

  // FE-BUG-001: Use functional setState to avoid stale closure
  useEffect(() => {
    setSelectedRows((prev) => {
      const newSelectedRow: AgencyProspect[] = [];
      prev.forEach((item) => {
        const index = list.findIndex((i) => i.id === item.id);
        if (index !== -1) {
          newSelectedRow.push(list[index]);
        }
      });
      return newSelectedRow;
    });
  }, [list]);

  const handleFetchData = (params: GetAgencyProspectListParams) => {
    dispatch(prospectActions.getAgencyProspectList(params));
  };

  const refetchData = () => handleFetchData({ ...queryParams });

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  // The BE only sorts when it gets both halves, so they are cleared together
  const onChangeTable = (sorter?: {
    field: string;
    order: 'desc' | 'asc';
  }) => {
    handleFetchData({
      ...queryParams,
      page: 0,
      sortField: sorter?.field || undefined,
      sortType: sorter?.order || undefined,
    });
  };

  const handleViewAgency = (data: AgencyProspect) => {
    setViewAgencyModal({
      open: true,
      data,
    });
  };

  // FE-BUG-002: Wrap in try/finally to always reset loading state
  const handleProgressRegistrationByIds = async (ids: string[]) => {
    setIsProgressRegistration(true);
    try {
      const params = { ids };
      await dispatch(
        prospectActions.progressRegistrationAgencyProspectById(params),
      ).unwrap();
      refetchData();
    } catch {
      showToast({
        message: messages['common.errorOccurred'] ?? 'An error occurred',
        type: 'error',
      });
    } finally {
      setIsProgressRegistration(false);
    }
  };

  const handleProgressRegistrationSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    handleProgressRegistrationByIds(selectedRows.map((row) => row.id));
  };

  // FE-BUG-004: Add confirmation dialog, FE-BUG-005: Add loading state for delete
  const handleDeleteAgencyByIds = async (ids: string[]) => {
    Modal.confirm({
      title: messages['common.confirm'] || 'Confirm',
      content: `Are you sure you want to delete ${ids.length} agency prospect(s)?`,
      onOk: async () => {
        setIsDeleting(true);
        try {
          const params = { ids };
          await dispatch(
            prospectActions.deleteAgencyProspectByIds(params),
          ).unwrap();
          refetchData();
        } catch {
          showToast({
            message: messages['common.errorOccurred'] ?? 'An error occurred',
            type: 'error',
          });
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const isFiltering = !!queryParams?.search;

  return (
    <AppCard>
      {(isProgressRegistration || isDeleting) && <AppLoader />}
      <AppsHeader>
        <ActionsHeader
          fetchData={handleFetchData}
          type={PROSPECT_TYPE.AGENCY}
          onProgressRegistrationSelected={handleProgressRegistrationSelected}
        />
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1000, y: 500 }}
          titleEmpty={
            messages['prospect.agency.empty'] ??
            messages['prospect.candidate.empty']
          }
          columns={getColumns({
            messages,
            prospectType: PROSPECT_TYPE.AGENCY,
            handleViewAgency,
            handleProgressRegistrationByIds,
            handleDeleteAgencyByIds,
          })}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
          rowSelection={{
            type: 'checkbox',
            onChange: (
              _selectedRowKeys: React.Key[],
              selectedRows: AgencyProspect[],
            ) => {
              setSelectedRows(selectedRows);
            },
          }}
          noDataType={isFiltering ? 'noDataFound' : 'noDataCreated'}
          addNewBtn={renderAddNewButton('outlined')}
        />
      </AppsContent>
      <ViewAgencyModal
        isOpen={viewAgencyModal.open}
        toggle={toggleViewAgencyModal}
        data={viewAgencyModal.data}
        refetchData={refetchData}
        handleProgressRegistrationByIds={handleProgressRegistrationByIds}
      />
    </AppCard>
  );
};

export default AgencyTab;
